<template>
  <v-card>
    <v-toolbar
      class="settings-window-toolbar"
      extension-height="40"
      flat
      height="80"
    >
      <v-avatar class="blue">
        <v-icon dark>
          {{ $t('Settings.Restaurant.Toolbar.Icon') }}
        </v-icon>
      </v-avatar>

      <v-toolbar-title class="pl-3">
        <div class="body-3">
          {{ $t('Settings.Restaurant.Toolbar.Title') }}
        </div>

        <div class="text-caption">
          {{ $t('Settings.Restaurant.Toolbar.Subtitle') }}
        </div>
      </v-toolbar-title>

      <v-spacer />

      <template #extension>
        <v-tabs
          v-model="tab"
          background-color="transparent"
          grow
          height="40"
          slider-color="blue"
        >
          <v-tab key="details">
            {{ $t('Settings.Restaurant.Tab.Details') }}
          </v-tab>

          <v-tab key="address">
            {{ $t('Settings.Restaurant.Tab.Address') }}
          </v-tab>

          <v-tab
            v-if="!vatInfoDisabled"
            key="company"
          >
            {{ $t('Settings.Restaurant.Tab.Company') }}
          </v-tab>
        </v-tabs>
      </template>
    </v-toolbar>

    <v-divider />

    <v-card-text
      id="settingsWindow"
      style="overflow-y: auto;"
    >
      <template v-if="viewData">
        <v-tabs-items v-model="tab">
          <v-tab-item key="details">
            <v-form
              ref="restaurantForm"
              v-model="restaurantFormValid"
              @submit.prevent
            >
              <v-card flat>
                <v-card-title>
                  <v-icon class="pr-3">
                    {{ $t('Settings.Restaurant.Field.CompanyDetails.Icon') }}
                  </v-icon>
                  <h5 class="text-body-2 font-weight-bold">
                    {{ $t('Settings.Restaurant.Field.CompanyDetails.Title') }}
                  </h5>
                </v-card-title>

                <v-divider />

                <v-card-text class="pa-3">
                  <v-container
                    class="pa-0"
                    fluid
                  >
                    <v-row dense>
                      <v-col cols="12">
                        <v-text-field
                          v-model="viewData.Restaurant.Name"
                          :counter="60"
                          :disabled="!userCanManage"
                          :hint="$t('Settings.Restaurant.Field.CompanyDetails.Name.Hint')"
                          :label="$t('Settings.Restaurant.Field.CompanyDetails.Name.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.CompanyDetails.Name.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="12">
                        <v-text-field
                          v-model="viewData.Restaurant.Desc"
                          :disabled="!userCanManage"
                          :hint="$t('Settings.Restaurant.Field.CompanyDetails.Description.Hint')"
                          :label="$t('Settings.Restaurant.Field.CompanyDetails.Description.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.CompanyDetails.Description.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="12">
                        <v-text-field
                          v-model="viewData.Restaurant.Email"
                          :counter="128"
                          :disabled="!userCanManage"
                          :hint="$t('Settings.Restaurant.Field.CompanyDetails.Email.Hint')"
                          :label="$t('Settings.Restaurant.Field.CompanyDetails.Email.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.CompanyDetails.Email.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="3">
                        <v-text-field
                          v-model="viewData.Restaurant.Phone1"
                          :counter="32"
                          :disabled="!userCanManage"
                          :hint="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Hint')"
                          :label="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Label')"
                          :max="32"
                          :min="5"
                          :prepend-icon="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="3">
                        <v-text-field
                          v-model="viewData.Restaurant.Phone2"
                          :counter="32"
                          :disabled="!userCanManage"
                          :hint="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Hint')"
                          :label="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Label')"
                          :max="32"
                          :min="5"
                          :prepend-icon="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="3">
                        <v-text-field
                          v-model="viewData.Restaurant.Phone3"
                          :counter="32"
                          :disabled="!userCanManage"
                          :hint="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Hint')"
                          :label="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Label')"
                          :max="32"
                          :min="5"
                          :prepend-icon="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="3">
                        <v-text-field
                          v-model="viewData.Restaurant.Phone4"
                          :counter="32"
                          :disabled="!userCanManage"
                          :hint="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Hint')"
                          :label="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Label')"
                          :max="32"
                          :min="5"
                          :prepend-icon="$t('Settings.Restaurant.Field.CompanyDetails.Phone.Icon')"
                          clearable
                          required
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>

              <v-card flat>
                <v-card-title>
                  <v-icon class="pr-3">
                    {{ $t('Settings.Restaurant.Field.CompanyDetails.Logo.Icon') }}
                  </v-icon>

                  <h5 class="text-body-2 font-weight-bold">
                    <lang-search-highlight
                      :lang-text="$t('Settings.Restaurant.Field.CompanyDetails.Logo.Title')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h5>
                </v-card-title>

                <v-divider />

                <v-card-text class="pa-3">
                  <v-container
                    class="pa-0"
                    fluid
                  >
                    <v-row dense>
                      <v-col
                        class="text-center"
                        cols="12"
                        sm="6"
                      >
                        <div>
                          <h5 class="text-body-2 font-weight-bold">
                            <lang-search-highlight
                              :lang-text="$t('Settings.Restaurant.Field.CompanyDetails.Logo.TitleLight')"
                              :search-text="langSearchText"
                              highlight
                            />
                          </h5>
                        </div>
                        <image-crop-selector
                          v-model="croppaLight"
                          :container-class="`transparent`"
                          :height="150"
                          :initial-image="restaurantLogoLight"
                          :initial-image-class="`pa-2`"
                          :initial-image-style="`border: 1px solid #1c1c1c !important; background-color: #262626;`"
                          :placeholder-color="`#ffffff`"
                          :placeholder-font-size="15"
                          :prevent-white-space="false"
                          :quality="1"
                          :replace-drop="true"
                          :show-loading="true"
                          :width="340"
                          @new-image-drawn="onPhotoLightChange"
                          @image-remove="onPhotoLightRemoved"
                        />
                      </v-col>

                      <v-col
                        class="text-center"
                        cols="12"
                        sm="6"
                      >
                        <div>
                          <h5 class="text-body-2 font-weight-bold">
                            <lang-search-highlight
                              :lang-text="$t('Settings.Restaurant.Field.CompanyDetails.Logo.TitleDark')"
                              :search-text="langSearchText"
                              highlight
                            />
                          </h5>
                        </div>
                        <image-crop-selector
                          v-model="croppaDark"
                          :container-class="`transparent`"
                          :height="150"
                          :initial-image="restaurantLogoDark"
                          :initial-image-class="`pa-2`"
                          :initial-image-style="`border: 1px solid #d7d7d7 !important; background-color: #fafafa;`"
                          :placeholder-color="`#000000`"
                          :placeholder-font-size="15"
                          :prevent-white-space="false"
                          :quality="1"
                          :replace-drop="true"
                          :show-loading="true"
                          :width="340"
                          @new-image-drawn="onPhotoDarkChange"
                          @image-remove="onPhotoDarkRemoved"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-form>
          </v-tab-item>

          <v-tab-item key="address">
            <v-form
              ref="addressForm"
              v-model="addressFormValid"
              @submit.prevent
            >
              <v-card flat>
                <v-card-title>
                  <v-icon class="pr-3">
                    {{ $t('Settings.Restaurant.Field.CompanyAddress.Icon') }}
                  </v-icon>
                  <h5 class="text-body-2 font-weight-bold">
                    <lang-search-highlight
                      :lang-text="$t('Settings.Restaurant.Field.CompanyAddress.Title')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h5>
                </v-card-title>

                <v-divider />

                <v-card-text class="pa-3">
                  <v-container
                    class="pa-0"
                    fluid
                  >
                    <v-row dense>
                      <v-col cols="12">
                        <v-google-autocomplete-service
                          v-model="autoCompleteModel"
                          :hint="$t('Settings.Restaurant.Field.CompanyAddress.Address.Hint')"
                          :placeholder="$t('Settings.Restaurant.Field.CompanyAddress.Address.PlaceHolder')"
                          :types="['geocode']"
                          autofocus
                          lazy
                          @place-changed="fillInAddressNoLimitis"
                        />
                      </v-col>

                      <v-col cols="12">
                        <gmap-map
                          ref="dialogMap"
                          :center="mapCenter"
                          :options="{styles: GMapStyles}"
                          :zoom="18"
                          map-type-id="roadmap"
                          style="width:100%; height: 225px"
                        >
                          <gmap-marker
                            ref="addressMarker"
                            :draggable="true"
                            :position="markerStore"
                            label="★"
                            @dragend="onMarkerDragEndNoLimits"
                          />
                        </gmap-map>
                      </v-col>

                      <v-col cols="12">
                        <v-row>
                          <v-col cols="12">
                            <v-switch
                              v-model="viewData.UseCustomAddress"
                              :label="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Label')"
                              class=""
                              color="primary"
                              hide-details
                              inset
                            />
                          </v-col>

                          <template v-if="!viewData.UseCustomAddress">
                            <v-col cols="12">
                              <v-google-autocomplete-service
                                v-model="viewData.Address.City"
                                :hint="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Region.Hint')"
                                :placeholder="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Region.PlaceHolder')"
                                :types="['(cities)']"
                                clearable
                                lazy
                                @blur="getAddressNoLimits"
                                @place-changed="fillInAddressNoLimitis"
                              />
                            </v-col>

                            <v-col cols="6">
                              <v-google-autocomplete-service
                                v-model="viewData.Address.Street"
                                :hint="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Street.Hint')"
                                :placeholder="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Street.PlaceHolder')"
                                :types="['address']"
                                clearable
                                lazy
                                @blur="getAddressNoLimits"
                                @place-changed="fillInAddressNoLimitis"
                              />
                            </v-col>

                            <v-col cols="3">
                              <v-text-field
                                v-model="viewData.Address.Number"
                                :label="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Number.Label')"
                                clearable
                                required
                                @blur="getAddressNoLimits"
                              />
                            </v-col>

                            <v-col cols="3">
                              <v-text-field
                                v-model="viewData.Address.Postcode"
                                :hint="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Postcode.Hint')"
                                :label="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Postcode.Label')"
                                clearable
                                required
                              />
                            </v-col>
                          </template>

                          <template v-else>
                            <v-col cols="12">
                              <div class="input-group input-group--prepend-icon input-group--text-field">
                                <div class="input-group__input">
                                  <v-text-field
                                    id="map_area"
                                    v-model="viewData.Address.City"
                                    :hint="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Region.Hint')"
                                    :placeholder="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Region.PlaceHolder')"
                                    :prepend-icon="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Region.Icon')"
                                    classname="form-control"
                                    clearable
                                    hide-details
                                    required
                                    types="(cities)"
                                  />
                                </div>
                              </div>
                            </v-col>

                            <v-col cols="6">
                              <div class="input-group input-group--prepend-icon input-group--text-field">
                                <div class="input-group__input">
                                  <v-text-field
                                    id="map_street"
                                    v-model="viewData.Address.Street"
                                    :hint="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Street.Hint')"
                                    :placeholder="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Street.PlaceHolder')"
                                    :prepend-icon="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Street.Icon')"
                                    classname="form-control"
                                    clearable
                                    hide-details
                                    required
                                    types="address"
                                  />
                                </div>
                              </div>
                            </v-col>

                            <v-col cols="3">
                              <v-text-field
                                v-model="viewData.Address.Number"
                                :label="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Number.Label')"
                                clearable
                                required
                              />
                            </v-col>

                            <v-col cols="3">
                              <v-text-field
                                v-model="viewData.Address.Postcode"
                                :hint="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Postcode.Hint')"
                                :label="$t('Settings.Restaurant.Field.CompanyAddress.CustomAddress.Postcode.Label')"
                                clearable
                                required
                              />
                            </v-col>
                          </template>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-form>
          </v-tab-item>

          <v-tab-item
            v-if="!vatInfoDisabled"
            key="company"
          >
            <v-form
              ref="vatInfoForm"
              v-model="vatInfoFormValid"
              @submit.prevent
            >
              <v-card flat>
                <v-card-title :class="$vuetify.theme.dark ? 'grey darken-2' : 'grey lighten-2'">
                  <v-icon class="pr-3">
                    {{ $t('Settings.Restaurant.Field.TaxInfo.Icon') }}
                  </v-icon>
                  <h5 class="text-body-2 font-weight-bold">
                    <lang-search-highlight
                      :lang-text="$t('Settings.Restaurant.Field.TaxInfo.Title')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h5>
                </v-card-title>

                <v-divider />

                <v-card-text class="pa-3">
                  <v-container
                    class="pa-0"
                    fluid
                  >
                    <v-row dense>
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.Company"
                          :counter="128"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Company.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Company.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Company.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.Profession"
                          :counter="128"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Profession.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Profession.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Profession.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="12">
                        <v-text-field
                          v-model="viewData.TaxInfo.DistinctiveTitle"
                          :counter="128"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.DistinctiveTitle.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.DistinctiveTitle.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.DistinctiveTitle.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.Irs"
                          :counter="128"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Irs.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Irs.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Irs.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        sm="3"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.Vat"
                          :counter="12"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Vat.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Vat.Label')"
                          :prefix="locale"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Vat.Icon')"
                          :rules="[(v) => v && validateVAT(locale, v) || $t('Customer.CustomerAddCompanyDialog.Field.VatNumber.Errors.AdvancedValid', {country: locale})]"
                          clearable
                          required
                        >
                          <template #append>
                            <v-btn
                              :disabled="vatInfoDisabled || !viewData.TaxInfo.Vat || viewData.TaxInfo.CountryId !== 86 || !locale || fetchingVatData || !validateVAT(locale, viewData.TaxInfo.Vat)"
                              :loading="fetchingVatData"
                              color="primary"
                              icon
                              small
                              @click="fetchDataByVatNumber"
                            >
                              <v-icon>mdi-database-arrow-down</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </v-col>

                      <v-col
                        cols="12"
                        sm="3"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.Gemi"
                          :counter="12"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Gemi.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Gemi.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Gemi.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        sm="9"
                      >
                        <v-autocomplete
                          v-model="viewData.TaxInfo.CountryId"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Country.Hint')"
                          :items="countryList"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Country.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Country.Icon')"
                          cache-items
                          class="pr-2"
                          clearable
                          item-text="short_name"
                          item-value="country_id"
                          required
                        >
                          <template #prepend>
                            <v-avatar
                              size="22"
                              tile
                            >
                              <template v-if="locale">
                                <v-img :src="`/assets/flags/1x1/${String(locale).toLowerCase()}.svg`" />
                              </template>
                              <template v-else>
                                <v-icon>map</v-icon>
                              </template>
                            </v-avatar>
                          </template>
                          <template #item="data">
                            <v-list-item-avatar size="34">
                              <v-img :src="`/assets/flags/1x1/${String(data.item.iso2).toLowerCase()}.svg`" />
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title v-safe-html="data.item.short_name" />
                              <v-list-item-subtitle
                                v-safe-html="data.item.long_name"
                                class="text-caption"
                              />
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>

                      <v-col
                        cols="12"
                        sm="3"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.City"
                          :counter="128"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.City.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.City.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.City.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        sm="9"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.Street"
                          :counter="128"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Street.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Street.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Street.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        sm="3"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.PostCode"
                          :counter="15"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.PostCode.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.PostCode.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.PostCode.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="12">
                        <v-text-field
                          v-model="viewData.TaxInfo.Phone"
                          v-mask="'################'"
                          :counter="16"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Phone.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Phone.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Phone.Icon')"
                          clearable
                          required
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>

              <v-card flat>
                <v-card-title :class="$vuetify.theme.dark ? 'grey darken-2' : 'grey lighten-2'">
                  <v-icon class="pr-3">
                    {{ $t('Settings.Restaurant.Field.TaxInfo.Branch.Icon') }}
                  </v-icon>
                  <h5 class="text-body-2 font-weight-bold">
                    <lang-search-highlight
                      :lang-text="$t('Settings.Restaurant.Field.TaxInfo.BranchTitle')"
                      :search-text="langSearchText"
                      highlight
                    />
                  </h5>
                </v-card-title>

                <v-divider />

                <v-card-text class="pa-3">
                  <v-container
                    class="pa-0"
                    fluid
                  >
                    <v-row dense>
                      <v-col
                        cols="12"
                        sm="4"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.Branch"
                          :counter="128"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Branch.Hint')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Branch.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Branch.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="12" />

                      <v-col
                        cols="12"
                        sm="4"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.BranchCity"
                          :counter="128"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.City.HintBranch')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.City.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.City.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        sm="5"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.BranchStreet"
                          :counter="128"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Street.HintBranch')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Street.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Street.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col
                        cols="12"
                        sm="3"
                      >
                        <v-text-field
                          v-model="viewData.TaxInfo.BranchPostCode"
                          :counter="15"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.PostCode.HintBranch')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.PostCode.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.PostCode.Icon')"
                          clearable
                          required
                        />
                      </v-col>

                      <v-col cols="12">
                        <v-text-field
                          v-model="viewData.TaxInfo.BranchPhone"
                          v-mask="'################'"
                          :counter="16"
                          :disabled="vatInfoDisabled"
                          :hint="$t('Settings.Restaurant.Field.TaxInfo.Phone.HintBranch')"
                          :label="$t('Settings.Restaurant.Field.TaxInfo.Phone.Label')"
                          :prepend-icon="$t('Settings.Restaurant.Field.TaxInfo.Phone.Icon')"
                          clearable
                          required
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-form>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4 settings-window-actions">
      <v-spacer />
      <v-btn
        class="light-blue--text text--darken-1"
        outlined
        to="/system-settings"
      >
        {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
      <v-btn
        v-if="userCanManage"
        :disabled="!viewData || isSaving"
        :loading="isSaving"
        class="green white--text elevation-0"
        @click="onSaveButtonClick"
      >
        {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import GMapAddressValidate from '@/mixins/gmapaddressvalidate'
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import ImageCropSelector from '@/components/common/ImageCropSelector'
import VGoogleAutocompleteService from '@/components/common/google/VGoogleAutocompleteService.vue'
import {API, APICall} from '@/api/Api'
import {validateVAT} from '@/lib/helper/helper.js'
import LangSearchHighlight from '@/lang/highlight/LangSearchHighlight.vue'
import LangSearchHighlightMixin from '@/lang/highlight/langSearchHighlightMixin.js'

export default {
  components: {
    LangSearchHighlight,
    VGoogleAutocompleteService,
    'image-crop-selector': ImageCropSelector
  },
  mixins: [AppData, Auth, GMapAddressValidate, LangSearchHighlightMixin],
  data() {
    return {
      isSaving           : false,
      tab                : null,
      restaurantFormValid: false,
      addressFormValid   : false,
      vatInfoFormValid   : false,
      tmpImageLight      : null,
      tmpImageDark       : null,
      tmpImageBase64Light: null,
      tmpImageBase64Dark : null,
      viewData           : null,
      fetchingVatData    : false,
      countriesArray     : [],
      croppaLight        : null,
      croppaDark         : null,
      validationRules    : {
        vatInfoForm: {
          Company: [
            (v) => v && v.length >= 2 && v.length <= 255 || this.$t('Faq.FaqCategoryAddDialog.Field.Title.Error.Between')
          ]
        }
      }
    }
  },
  computed: {
    locale() {
      const country = this.countriesArray.find(country => country.country_id === this.viewData?.TaxInfo?.CountryId)
      return country ? (country.iso2 === 'GR' ? 'EL' : country.iso2 === 'GB' ? 'UK' : country.iso2) : ''
    },

    countryList() {
      return this.countriesArray || []
    },

    vatInfoDisabled() {
      return !this.userInAdminGroup
    },

    GMapStyles() {
      if (!this.isDarkMode) {
        return [
          {
            featureType: 'poi',
            stylers    : [{visibility: 'off'}]
          }
        ]
      }

      return [
        {
          featureType: 'poi',
          stylers    : [{visibility: 'off'}]
        },
        {
          elementType: 'geometry',
          stylers    : [{color: '#242f3e'}]
        },
        {
          elementType: 'labels.text.stroke',
          stylers    : [{color: '#242f3e'}]
        },
        {
          elementType: 'labels.text.fill',
          stylers    : [{color: '#746855'}]
        },
        {
          featureType: 'administrative.locality',
          elementType: 'labels.text.fill',
          stylers    : [{color: '#d59563'}]
        },
        {
          featureType: 'road',
          elementType: 'geometry',
          stylers    : [{color: '#38414e'}]
        },
        {
          featureType: 'road',
          elementType: 'geometry.stroke',
          stylers    : [{color: '#212a37'}]
        },
        {
          featureType: 'road',
          elementType: 'labels.text.fill',
          stylers    : [{color: '#9ca5b3'}]
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry',
          stylers    : [{color: '#746855'}]
        },
        {
          featureType: 'road.highway',
          elementType: 'geometry.stroke',
          stylers    : [{color: '#1f2835'}]
        },
        {
          featureType: 'road.highway',
          elementType: 'labels.text.fill',
          stylers    : [{color: '#f3d19c'}]
        },
        {
          featureType: 'transit',
          elementType: 'geometry',
          stylers    : [{color: '#2f3948'}]
        },
        {
          featureType: 'transit.station',
          elementType: 'labels.text.fill',
          stylers    : [{color: '#d59563'}]
        },
        {
          featureType: 'water',
          elementType: 'geometry',
          stylers    : [{color: '#17263c'}]
        },
        {
          featureType: 'water',
          elementType: 'labels.text.fill',
          stylers    : [{color: '#515c6d'}]
        },
        {
          featureType: 'water',
          elementType: 'labels.text.stroke',
          stylers    : [{color: '#17263c'}]
        }
      ]
    },

    restaurantLogoLight() {
      return this.viewData.Restaurant.Image ? this.appConfig.LOCATION_DATA.CdnImagesUrl + this.viewData.Restaurant.Image : ''
    },
    restaurantLogoDark() {
      return this.viewData.Restaurant.ImageDark ? this.appConfig.LOCATION_DATA.CdnImagesUrl + this.viewData.Restaurant.ImageDark : ''
    },

    newAddress: {
      get() {
        return this.viewData.Address
      },
      set(data) {
        this.viewData.Address = data
      }
    }
  },
  watch: {},
  created() {
    // console.log('1. created');
  },
  mounted() {
    // console.log('2. mounted');
    this.$bus.$on(window.SocketCommand.Settings.Restaurant.Get, this.onGetRestaurantResult)
    this.$bus.$on(window.SocketCommand.Settings.Restaurant.Save, this.onSaveRestaurantResult)
    this.getData()
  },
  updated() {
    // console.log('3. updated');
  },
  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.Settings.Restaurant.Get, this.onGetRestaurantResult)
    this.$bus.$off(window.SocketCommand.Settings.Restaurant.Save, this.onSaveRestaurantResult)
  },
  methods: {
    validateVAT,
    onGetRestaurantResult(result) {
      this.viewData = {
        Address         : result.Address,
        Restaurant      : result.Restaurant,
        TaxInfo         : result.TaxInfo,
        UseCustomAddress: result.UseCustomAddress
      }
      this.countriesArray = result.Countries || []
      this.autoCompleteModel = this?.viewData?.Address?.Formatted || ''
    },
    getData() {
      window.callAS(window.SocketCommand.Settings.Restaurant.Get, {})
    },

    async fetchDataByVatNumber() {
      if (!this.viewData.TaxInfo.Vat || this.viewData?.TaxInfo?.CountryId !== 86 || !this.locale) return

      this.fetchingVatData = true

      const payload = {
        Vat           : this.viewData.TaxInfo.Vat,
        VatCountryCode: this.locale || 'EL'
      }

      try {
        const response = await API.post(APICall.vatCheck, payload)
        const data = response?.data || null
        const status = data?.status || ''

        if (status === 'success') {
          this.setCompanyDataFromApi(data)
        }
      } catch (e) {

        console.log(e)
      }

      this.fetchingVatData = false
    },

    setCompanyDataFromApi(data) {
      this.viewData.TaxInfo.Irs = data.IrsDescription || ''
      this.viewData.TaxInfo.Company = data.BrandName || ''
      this.viewData.TaxInfo.DistinctiveTitle = data.DistinctiveTitle || ''
      this.viewData.TaxInfo.Profession = data.Profession || ''
      this.viewData.TaxInfo.Street = `${data.Street || ''} ${data.StreetNumber || ''}`
      this.viewData.TaxInfo.PostCode = data.Postcode || ''
      this.viewData.TaxInfo.City = data.City || ''
    },

    onSaveButtonClick() {
      this.isSaving = true
      this.onPhotoLightChange()
      this.onPhotoDarkChange()
      this.viewData.Restaurant.ImageBase64 = this.viewData.Restaurant.Image ? null : this.tmpImageBase64Light ? this.tmpImageBase64Light.img : ''
      this.viewData.Restaurant.ImageMime = this.tmpImageBase64Light ? this.tmpImageBase64Light.mime : ''
      this.viewData.Restaurant.ImageExt = this.tmpImageBase64Light ? this.tmpImageBase64Light.ext : ''

      this.viewData.Restaurant.ImageBase64Dark = this.viewData.Restaurant.ImageDark ? null : this.tmpImageBase64Dark ? this.tmpImageBase64Dark.img : ''
      this.viewData.Restaurant.ImageMimeDark = this.tmpImageBase64Dark ? this.tmpImageBase64Dark.mime : ''
      this.viewData.Restaurant.ImageExtDark = this.tmpImageBase64Dark ? this.tmpImageBase64Dark.ext : ''

      window.callAS(window.SocketCommand.Settings.Restaurant.Save, this.viewData, '', 300000)
    },

    onSaveRestaurantResult(data) {
      this.isSaving = false

      if (data.status === 'success') {
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: data.type,
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Restaurant.Notification'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    onPhotoLightChange() {
      if (this.croppaLight && this.croppaLight.getChosenFile() && !this.viewData.Restaurant.Image) {
        this.tmpImageLight = this.croppaLight.getChosenFile()
        this.tmpImageBase64Light = {
          img : this.croppaLight.generateDataUrl(),
          mime: this.tmpImageLight.type,
          ext : this.tmpImageLight.name.split('.')[1]
        }
      }
    },
    onPhotoLightRemoved() {
      this.viewData.Restaurant.Image = ''
      this.tmpImageLight = null
      this.tmpImageBase64Light = null
    },

    onPhotoDarkChange() {
      if (this.croppaDark && this.croppaDark.getChosenFile() && !this.viewData.Restaurant.ImageDark) {
        this.tmpImageDark = this.croppaDark.getChosenFile()
        this.tmpImageBase64Dark = {
          img : this.croppaDark.generateDataUrl(),
          mime: this.tmpImageDark.type,
          ext : this.tmpImageDark.name.split('.')[1]
        }
      }
    },
    onPhotoDarkRemoved() {
      this.viewData.Restaurant.ImageDark = ''
      this.tmpImageDark = null
      this.tmpImageBase64Dark = null
    }
  }
}
</script>

<style scoped>
:deep(.picture-input .picture-inner-text) {
  font-size: 12px !important;
}
</style>
